import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Fingerprint, Network, Building2, Tags, GitBranch, Rss, Braces, Cpu, Plug, Store } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { useStore } from "@/context/StoreProvider";
import AiScore from "@/components/common/AiScore";
import KnowledgeGraphView from "@/components/common/KnowledgeGraphView";

function Panel({ icon: Icon, title, count, children, testid }) {
  return (
    <section className="rounded-lg border border-border bg-card p-5" data-testid={testid}>
      <h3 className="mb-3 flex items-center justify-between font-display text-sm font-bold uppercase tracking-wide text-muted-foreground">
        <span className="flex items-center gap-2"><Icon className="h-4 w-4 text-brand" />{title}</span>
        {count != null && <span className="font-mono text-brand">{count}</span>}
      </h3>
      {children}
    </section>
  );
}

export default function AICommerceIdentity() {
  const t = useT();
  const { store, loading } = useStore();
  const [id, setId] = useState(null);

  useEffect(() => {
    if (store) api.get(`/identity/${store.id}`).then(({ data }) => setId(data)).catch(() => {});
  }, [store]);

  if (loading) return <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" />;
  if (!store) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <Store className="mx-auto h-10 w-10 text-muted-foreground" />
        <p className="mt-4 text-muted-foreground">{t("dashboard.home.noStore")}</p>
        <Link to="/dashboard/store" className="mt-4 inline-flex rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground">{t("dashboard.home.noStoreCta")}</Link>
      </div>
    );
  }

  return (
    <div>
      <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold tracking-tighter">
        <Fingerprint className="h-6 w-6 text-brand" />{t("identity.title")}
      </h1>
      <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t("identity.subtitle")}</p>

      {id && (
        <>
          <div className="mt-6 rounded-lg border border-border bg-secondary/30 p-5">
            <p className="text-sm italic leading-relaxed text-muted-foreground">"{t("identity.manifesto")}"</p>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
            <div className="min-w-0 space-y-4">
              <div className="flex flex-col items-center rounded-lg border border-border bg-primary p-8 text-primary-foreground">
                <span className="text-xs uppercase tracking-widest text-primary-foreground/60">{t("identity.score")}</span>
                <div className="my-4"><AiScore score={id.identity_score} size="lg" /></div>
                <p className="text-center text-xs text-primary-foreground/60">{t("identity.products", { count: id.catalog.product_count })}</p>
              </div>
              <Panel icon={Cpu} title={t("identity.summary")} testid="identity-summary">
                <p className="text-sm leading-relaxed">{id.ai_summary}</p>
              </Panel>
            </div>

            <div className="min-w-0 space-y-6">
              <Panel icon={Network} title={t("identity.knowledgeGraph")} testid="identity-graph">
                <KnowledgeGraphView graph={id.knowledge_graph} />
              </Panel>

              <div className="grid gap-6 md:grid-cols-2">
                <Panel icon={Building2} title={t("identity.brandEntities")} count={id.brand_entities.length} testid="identity-brands">
                  <div className="flex flex-wrap gap-2">
                    {id.brand_entities.map((b) => (
                      <span key={b.name} className="rounded-md border border-border bg-secondary/50 px-2.5 py-1 text-sm">{b.name} <span className="text-xs text-muted-foreground">×{b.product_count}</span></span>
                    ))}
                  </div>
                </Panel>
                <Panel icon={Tags} title={t("identity.semanticCategories")} count={id.semantic_categories.length} testid="identity-categories">
                  <div className="flex flex-wrap gap-2">
                    {id.semantic_categories.map((c) => (
                      <span key={c.name} className="rounded-md bg-brand/10 px-2.5 py-1 text-sm text-brand">{c.name} <span className="text-xs">×{c.count}</span></span>
                    ))}
                  </div>
                </Panel>
              </div>

              <Panel icon={GitBranch} title={t("identity.productRelations")} count={id.product_relations.length} testid="identity-relations">
                <ul className="space-y-1.5 text-sm">
                  {id.product_relations.slice(0, 8).map((r, i) => (
                    <li key={i} className="text-muted-foreground"><span className="text-foreground">{r.product}</span> <span className="text-brand">{t("identity.relatedTo")}</span> {r.related.join(", ")}</li>
                  ))}
                </ul>
              </Panel>

              <Panel icon={Tags} title={t("identity.semanticKeywords")} count={id.semantic_keywords.length} testid="identity-keywords">
                <div className="flex flex-wrap gap-1.5">
                  {id.semantic_keywords.slice(0, 40).map((k, i) => (
                    <span key={i} className="rounded bg-secondary px-2 py-0.5 font-mono text-xs text-muted-foreground">{k}</span>
                  ))}
                </div>
              </Panel>

              <div className="grid gap-6 md:grid-cols-2">
                <Panel icon={Cpu} title={t("identity.protocols")} testid="identity-protocols">
                  <div className="space-y-2">
                    {id.protocols.map((p) => (
                      <div key={p.key} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                        {p.label}<span className="rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium text-success">{t("connectorsPage.statusReady")}</span>
                      </div>
                    ))}
                  </div>
                </Panel>
                <Panel icon={Plug} title={t("identity.connectors")} testid="identity-connectors">
                  <div className="space-y-2">
                    {id.connectors.filter((c) => c.status === "ready").map((c) => (
                      <div key={c.key} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                        {c.label}<span className="rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium text-success">{t("connectorsPage.statusReady")}</span>
                      </div>
                    ))}
                  </div>
                </Panel>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
